import { FaPhone, FaCalendar, FaClock, FaUser, FaMicrophone, FaUserAlt, FaMicrophoneSlash, FaQuestionCircle, FaTimes, FaFileInvoiceDollar, FaBoxOpen } from "react-icons/fa";
import { FaNoteSticky } from "react-icons/fa6";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import IdButton from "../ui/components/IdButton";
import CopyToClipBoard from "../../utils/CopyToClipBoard";
import Modal from "../ui/Modal"
import { useState, useEffect } from "react";
import callReasons from "../../utils/plainValues/CallReasons";
import { FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Checkbox, CircularProgress } from "@mui/material";
import InputFormValue from "../ui/components/InputFormValue";
import { BACKEND } from "../../config/configEnvs";
import axios from "axios";
import { toast } from "react-toastify";
import ModalInDrawer from "../ui/ModalInDrawer";

const CallCard = ({ call, services, refetch = null}) => {
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mainQuote, setMainQuote] = useState(call.quotes.length > 0 ? call.quotes[0] : "");
  const [selectedReasons, setSelectedReasons] = useState(call.reason ? call.reason.split(', ') : []);

  const [notes, setNotes] = useState(call.notes || "");
  const [reason, setReason] = useState(call.reason || "");
  const [quotes, setQuotes] = useState(
    call.quotes.length > 1 ? 
      call.quotes.slice(1).map((q) => q.id).join(', ') : 
      ''
  );
  
  const [servicesOfInterest, setServicesOfInterest] = useState(call.servicesOfInterest || "");

  const [selectedServiceIds, setSelectedServiceIds] = useState(() => {
    if (!call.servicesOfInterest || !services || services.length === 0) {
      return [];
    }
    
    // Si servicesOfInterest es un string, convertirlo a array
    const servicesOfInterestArray = typeof call.servicesOfInterest === 'string' 
      ? call.servicesOfInterest.split(', ').filter(s => s.trim() !== '')
      : call.servicesOfInterest;
    
    // Mapear nombres de servicios a IDs
    const selectedIds = services
      .filter(service => servicesOfInterestArray.includes(service.name))
      .map(service => service.id);
    
    return selectedIds;
  });

  const confirmData = async () => {
    try {
      // Validar y filtrar solo IDs válidos de cotizaciones adicionales
      const additionalQuotes = quotes.trim() ? 
        quotes.split(',').map(id => id.trim()).filter(id => id !== '') : [];
      
      const validAdditionalQuotes = additionalQuotes.filter(id => 
        id.length === 24 && /^[a-fA-F0-9]{24}$/.test(id)
      );

      // Construir array final de cotizaciones (principal + adicionales válidas)
      const allQuotes = [];
      if (mainQuote && mainQuote.id) {
        allQuotes.push(mainQuote.id);
      }
      allQuotes.push(...validAdditionalQuotes);

      await axios.put(`${BACKEND}/api/registroLlamadas/${call.id}`, {
        reason: reason,
        notes: notes,
        quotes: allQuotes,
        servicesOfInterest: servicesOfInterest,
        confirmedData: true
      });

      setIsModalOpen(false);
      toast.success("Información actualizada correctamente");

      if (refetch) {
        refetch();
      }
    } catch (e) {
      console.error("Error al confirmar la información de la llamada: ", e);
    }
  }


  const handleReasonsChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedReasons(selectedValues);
    const reasonsString = selectedValues.join(', ');
    setReason(reasonsString);
  };

  const handleServicesChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedServiceIds(selectedValues);
    
    // Convertir los IDs seleccionados a nombres de servicios y almacenar como string
    const selectedServiceNames = services
      .filter(service => selectedValues.includes(service.id))
      .map(service => service.name);
    
    const servicesString = selectedServiceNames.join(', ');
    setServicesOfInterest(servicesString);
  };

  const isServiceSelected = (serviceId) => {
    return selectedServiceIds.includes(serviceId);
  };

  // Sincronizar estados cuando cambien los datos de la llamada
  useEffect(() => {
    // Sincronizar servicios de interés
    if (!call.servicesOfInterest || !services || services.length === 0) {
      setSelectedServiceIds([]);
    } else {
      // Si servicesOfInterest es un string, convertirlo a array
      const servicesOfInterestArray = typeof call.servicesOfInterest === 'string' 
        ? call.servicesOfInterest.split(', ').filter(s => s.trim() !== '')
        : call.servicesOfInterest;
      
      // Mapear nombres de servicios a IDs
      const selectedIds = services
        .filter(service => servicesOfInterestArray.includes(service.name))
        .map(service => service.id);
      
      setSelectedServiceIds(selectedIds);
    }

    // Sincronizar otros estados
    setNotes(call.notes || "");
    setReason(call.reason || "");
    setSelectedReasons(call.reason ? call.reason.split(', ') : []);
    setServicesOfInterest(call.servicesOfInterest || "");
    
    // Sincronizar cotizaciones adicionales
    const additionalQuotes = call.quotes.length > 1 ? 
      call.quotes.slice(1).map((q) => q.id).join(', ') : 
      '';
    setQuotes(additionalQuotes);
    
  }, [call, services]);

  return (
    <div className="bg-white p-3 w-full rounded-lg shadow-sm border border-gray-200 mb-3 hover:shadow-md transition-shadow">

      <ModalInDrawer 
        title="Confirmar información de llamada" 
        activeModal={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        centered
        className="max-w-4xl"
      >
        <div className="flex flex-col gap-4 max-h-[80vh] overflow-y-auto">

          {/* Información de la llamada - Solo lectura */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-xs font-semibold text-gray-800 mb-3">Información de la llamada</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

              {/* Número llamado */}
              <div className="flex items-center gap-2">
                <FaPhone className="text-blue-500 text-sm" />
                <div>
                  <label className="text-xs text-gray-600 block">Número llamado</label>
                  <span className="text-xs font-medium text-gray-800">
                    {call.numberCalled || "Sin número"}
                  </span>
                </div>
              </div>

              {/* Duración de la llamada */}
              <div className="flex items-center gap-2">
                <FaClock className="text-green-500 text-sm" />
                <div>
                  <label className="text-xs text-gray-600 block">Duración</label>
                  <span className="text-xs font-medium text-gray-800">
                    {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : "No disponible"}
                  </span>
                </div>
              </div>

              {/* Cliente */}
              <div className="flex items-center gap-2">
                <FaUser className="text-purple-500 text-sm" />
                <div>
                  <label className="text-xs text-gray-600 block">Cliente</label>
                  <span className="text-xs font-medium text-gray-800">
                    {call.client?.nombreComercial || "Sin cliente"}
                  </span>
                </div>
              </div>

              {/* Categoría */}
              <div className="flex items-center gap-2">
                <FaCalendar className="text-orange-500 text-sm" />
                <div>
                  <label className="text-xs text-gray-600 block">Categoría</label>
                  <span className="text-xs font-medium text-gray-800">
                    {call.category || "No especificada"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cotización principal - Solo lectura */}
          {call.quotes && call.quotes.length > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <FaFileInvoiceDollar className="text-blue-600" />
                Cotización principal
              </h3>
              <div className="flex items-center gap-2">
                <IdButton 
                  content={call.quotes[0].id} 
                  action={() => CopyToClipBoard(call.quotes[0].id)} 
                />
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  Principal (no se puede eliminar)
                </span>
              </div>
            </div>
          )}

          {/* Motivos de la llamada */}
          <div>
            <label className="flex flex-row items-center text-xs block text-gray-700 mb-1">
              Motivos de la llamada
            </label>
            <div className="flex">
              <span className="text-xs inline-flex items-center px-3 text-gray-900 bg-gray-200 border rounded-e-0 border-gray-300 border-e-0 rounded-s-md">
                <FaQuestionCircle />
              </span>
              <FormControl sx={{ flex: 1, minWidth: 0 }}>
                <Select
                  multiple
                  value={selectedReasons}
                  onChange={handleReasonsChange}
                  input={
                    <OutlinedInput 
                      sx={{ 
                        fontSize: '0.875rem',
                        borderRadius: '0 0.5rem 0.5rem 0',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #d1d5db',
                        color: '#111827',
                        height: '42px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: '2px solid #3b82f6'
                        },
                        '& .MuiSelect-select': {
                          padding: '0.625rem',
                          fontSize: '0.875rem',
                          color: '#111827'
                        }
                      }}
                    />
                  }
                  renderValue={(selected) => {
                    if (selected.length === callReasons.length) return "Todos los motivos";
                    if (selected.length === 0) return "Seleccionar motivos";
                    return selected.join(', ');
                  }}
                  displayEmpty
                  MenuProps={{
                    disablePortal: false,
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 6.5,
                        width: 250,
                        zIndex: 99999,
                        position: 'fixed'
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    style: {
                      zIndex: 99999
                    }
                  }}
                >
                  {callReasons.map((reason, index) => {
                    const isSelected = selectedReasons.includes(reason.value);
                    return (
                      <MenuItem key={index} value={reason.value} sx={{ height: "40px", fontSize: '0.875rem' }}>
                        <Checkbox 
                          checked={isSelected} 
                          sx={{ padding: '4px' }}
                        />
                        <ListItemText 
                          primary={reason.label} 
                          primaryTypographyProps={{ 
                            fontSize: '0.875rem', 
                            fontFamily: "Poppins, sans-serif",
                            color: '#111827'
                          }}
                        />
                      </MenuItem>
                    );
                    })}
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Servicios relacionados */}
          <div>
            <label className="flex flex-row items-center text-xs block text-gray-700 mb-1">
              Servicios relacionados
            </label>
            <div className="flex">
              <span className="text-xs inline-flex items-center px-3 text-gray-900 bg-gray-200 border rounded-e-0 border-gray-300 border-e-0 rounded-s-md">
                <FaBoxOpen />
              </span>
              <FormControl sx={{ flex: 1, minWidth: 0 }}>
                <Select
                  multiple
                  value={selectedServiceIds}
                  onChange={handleServicesChange}
                  input={
                    <OutlinedInput 
                      sx={{ 
                        fontSize: '0.875rem',
                        borderRadius: '0 0.5rem 0.5rem 0',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #d1d5db',
                        color: '#111827',
                        height: '42px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: '2px solid #3b82f6'
                        },
                        '& .MuiSelect-select': {
                          padding: '0.625rem',
                          fontSize: '0.875rem',
                          color: '#111827'
                        }
                      }}
                    />
                  }
                  renderValue={(selected) => {
                    if (selected.length === services.length) return "Todos los servicios";
                    if (selected.length === 0) return "Seleccionar servicios";
                    
                    // Obtener los nombres de servicios basados en los IDs seleccionados
                    const selectedServiceNames = services
                      .filter(service => selected.includes(service.id))
                      .map(service => service.name);
                    
                    return selectedServiceNames.join(', ');
                  }}
                  displayEmpty
                  MenuProps={{
                    disablePortal: false,
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 6.5,
                        width: 250,
                        zIndex: 99999,
                        position: 'fixed'
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    style: {
                      zIndex: 99999
                    }
                  }}
                >
                  {services && services?.length > 0 && services.map((service, index) => {
                    const isSelected = isServiceSelected(service.id);
                    return (
                      <MenuItem key={service.id} value={service.id} sx={{ height: "40px", fontSize: '0.875rem' }}>
                        <Checkbox 
                          checked={isSelected} 
                          sx={{ padding: '4px' }}
                        />
                        <ListItemText 
                          primary={service.name} 
                          primaryTypographyProps={{ 
                            fontSize: '0.875rem', 
                            fontFamily: "Poppins, sans-serif",
                            color: '#111827'
                          }}
                        />
                      </MenuItem>
                    );
                    })}
                </Select>
              </FormControl>
            </div>
          </div>


          {/* Cotizaciones adicionales */}
          <div>
            <InputFormValue
              title="Cotizaciones adicionales"
              info="IDs de cotizaciones adicionales (ObjectId de 24 caracteres) separados por comas"
              value={quotes}
              setValue={(value) => {
                // Actualizar el campo de input sin filtrar
                setQuotes(value);
                // Procesar IDs en tiempo real solo para mostrar preview
                // processQuotesInput(value);
              }}
              icon={<FaFileInvoiceDollar />}
              placeholder="689145b3eb54134dedc9814c, 689145b3eb54134dedc9814d"
              type="text"
            />
            
            {/* Mostrar preview de IDs ingresados (válidos e inválidos) */}
            {quotes && (
              (() => {
                const inputValue = quotes;
                if (!inputValue.trim()) return null;
                
                const allIds = inputValue.split(',').map(id => id.trim()).filter(id => id !== '');
                const validIds = allIds.filter(id => id.length === 24 && /^[a-fA-F0-9]{24}$/.test(id));
                const invalidIds = allIds.filter(id => !(id.length === 24 && /^[a-fA-F0-9]{24}$/.test(id)));
                
                if (allIds.length === 0) return null;
                
                return (
                  <div className="mt-2">
                    {validIds.length > 0 && (
                      <div className="mb-2">
                        <label className="text-xs text-green-600 mb-1 block">
                          IDs válidos ({validIds.length}):
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {validIds.map((validId, index) => (
                            <div
                              key={`valid-${index}`}
                              className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-200"
                            >
                              <FaFileInvoiceDollar className="mr-1" />
                              {validId.length > 20 ? `${validId.substring(0, 20)}...` : validId}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {invalidIds.length > 0 && (
                      <div className="mb-2">
                        <label className="text-xs text-red-600 mb-1 block">
                          IDs inválidos ({invalidIds.length}):
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {invalidIds.map((invalidId, index) => (
                            <div
                              key={`invalid-${index}`}
                              className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full border border-red-200"
                            >
                              <FaTimes className="mr-1" />
                              {invalidId.length > 20 ? `${invalidId.substring(0, 20)}...` : invalidId}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
              
            {/* Resumen de IDs procesados */}
            {quotes && (() => {
              const inputValue = quotes;
              if (!inputValue.trim()) return null;
              
              const allIds = inputValue.split(',').map(id => id.trim()).filter(id => id !== '');
              const validIds = allIds.filter(id => id.length === 24 && /^[a-fA-F0-9]{24}$/.test(id));
              
              if (validIds.length > 0) {
                return (
                  <div className="mt-2">
                    <label className="text-xs text-blue-600 mb-1 block">
                      Resumen: {validIds.length} ID(s) válido(s) de {allIds.length} total
                    </label>
                  </div>
                );
              }
              return null;
            })()}

          </div>

          {/* Notas */}
          <div>
            <InputFormValue
              title="Notas"
              info="Notas adicionales sobre la llamada"
              value={notes}
              setValue={setNotes}
              icon={<FaNoteSticky />}
              placeholder="Agregar notas sobre la llamada..."
              type="textarea"
            />
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3 pt-4 border-t">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
            <button 
              onClick={confirmData}
              className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium"
            >
              Confirmar información
            </button>
          </div>

        </div>
      </ModalInDrawer>

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FaPhone className="text-blue-500 text-xs" />
          <span className="text-xs font-medium text-gray-700">
            {call.numberCalled || "Sin número"}
          </span>
        </div>
        <span className="text-xs text-gry-500">
          {new Date(call.createdAt).toLocaleString('es-ES', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>

      <p className="text-xs text-black font-semibold bg-orange-400/50 rounded text-center mb-2">
        Categoría: {call.category}
      </p>
      
      {call.reason && (
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
          <strong>Motivo:</strong> {call.reason}
        </p>
      )}

      {call.quotes && call.quotes.length > 0 && (
        <div className="flex flex-row gap-2 items-center text-xs text-gray-600 mb-2 line-clamp-2">
          <strong>Cotizaciones vinculadas:</strong> 
          {call.quotes.map((q, i) => (
            <span key={q.id} className="text-xs text-gray-600">
              <IdButton key={i} content={q.id} action={() => CopyToClipBoard(q.id)} />
            </span>
          ))}
        </div>
      )}

      {call.notes && (
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
          <strong>Notas:</strong> {call.notes}
        </p>
      )}

      {call.servicesOfInterest && (
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
          <strong>Servicios de interés:</strong> {call.servicesOfInterest}
        </p>
      )}

      {call.worker && (
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
            {call.worker.foto ? (
              <img 
                src={call.worker.foto.url} 
                alt="Trabajador"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <FaUserAlt className="text-gray-500 text-xs" />
            )}
          </div>
          <span className="text-xs text-gray-700">
            {call.worker.name || "Sin trabajador"}
          </span>
        </div>
      )}

      {call.client && (
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
            <FaUserAlt className="text-blue-500 text-xs" />
          </div>
          <span className="text-xs text-gray-700">
            Cliente: {call.client.nombreComercial || "Sin cliente"}
          </span>
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <FaCalendar className="text-gray-400" />
          <span>
            {call.createdAt && format(new Date(call.createdAt), "dd/MM/yyyy", { locale: es })}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <FaClock className="text-gray-400" />
          <span>
            {call.createdAt && format(new Date(call.createdAt), "HH:mm", { locale: es })}
          </span>
        </div>
      </div>

      {call.url ? (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FaMicrophone className="text-xs text-green-500" />
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 w-fit">
              Con audio
            </span>
          </div>
          <audio src={call.url} controls className="w-full h-12" />
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FaMicrophoneSlash className="text-xs text-red-500" />
            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 w-fit">
              Sin audio
            </span>
          </div>
        </div>
      )}

      {call.confirmedData ? (
        <div className="text-xs w-full text-white bg-green-500 mt-2 py-1 font-semibold rounded-lg text-center mb-2">
          <span>Información confirmada</span>
        </div>  
      ) : (
        <button className="text-xs w-full text-white bg-red-500 hover:bg-red-600 transition-all duration-100 mt-2 py-1 font-semibold rounded-lg text-center mb-2" onClick={() => setIsModalOpen(true)}>
          <span>Confirmar información</span>
        </button>
      )}
    </div>
  )
}

export default CallCard;